import { calculateOverflowAmount, detectOverflow } from '@/utils/overflowDetection';
import { scrollIntoViewIfNeeded } from '@/utils/scrollHelpers';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

/**
 * Configuration options for smart collapse behavior
 */
export interface SmartCollapseConfig {
  enabled?: boolean;
  collapseStrategy?: 'all' | 'incremental';
  scrollBehavior?: ScrollBehavior;
  debounceMs?: number;
}

/**
 * Options for the useSmartCollapse hook
 */
interface SmartCollapseOptions extends SmartCollapseConfig {
  containerRef: RefObject<HTMLElement>;
  onCollapse?: (sectionId: string) => void;
  onExpand?: (sectionId: string) => void;
  initialOpenSections?: string[];
}

/**
 * Section metadata tracked by the hook
 */
interface SectionMetadata {
  id: string;
  element: HTMLElement | null;
  lastUsed: number;
  isOpen: boolean;
}

/**
 * Return type for the useSmartCollapse hook
 */
interface SmartCollapseReturn {
  openSections: Set<string>;
  expandSection: (sectionId: string, element?: HTMLElement) => void;
  collapseSection: (sectionId: string) => void;
  toggleSection: (sectionId: string, element?: HTMLElement) => void;
  registerSection: (sectionId: string, element: HTMLElement | null) => void;
  isSectionOpen: (sectionId: string) => boolean;
}

const DEFAULT_CONFIG: Required<SmartCollapseConfig> = {
  enabled: true,
  collapseStrategy: 'incremental',
  scrollBehavior: 'smooth',
  debounceMs: 350, // Increased to account for collapse animation (300ms + buffer)
};

/**
 * Hook for managing smart collapsible sections with LRU-based auto-collapse
 * 
 * Features:
 * - Tracks which sections are open/closed
 * - Maintains LRU (Least Recently Used) timestamps
 * - Detects overflow and auto-collapses sections in LRU order
 * - Smooth scrolls to keep expanded sections visible
 * - Handles dynamic content with ResizeObserver
 */
export function useSmartCollapse(options: SmartCollapseOptions): SmartCollapseReturn {
  const config = { ...DEFAULT_CONFIG, ...options };
  const { containerRef, onCollapse, onExpand } = options;

  // Track section metadata
  const sectionsRef = useRef<Map<string, SectionMetadata>>(new Map());
  const [openSections, setOpenSections] = useState<Set<string>>(
    new Set(options.initialOpenSections || [])
  );
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const pendingCheckRef = useRef<number | null>(null);

  /**
   * Registers a section with the hook
   */
  const registerSection = useCallback((sectionId: string, element: HTMLElement | null) => {
    const sections = sectionsRef.current;
    const existing = sections.get(sectionId);
    
    sections.set(sectionId, {
      id: sectionId,
      element,
      lastUsed: existing?.lastUsed || Date.now(),
      isOpen: existing?.isOpen || openSections.has(sectionId),
    });
  }, [openSections]);

  /**
   * Checks for overflow and collapses sections if needed
   */
  const checkOverflowAndCollapse = useCallback(
    (expandedSectionId: string) => {
      if (!config.enabled || !containerRef.current) return;

      const container = containerRef.current;
      const sections = sectionsRef.current;
      const expandedSection = sections.get(expandedSectionId);

      if (!expandedSection?.element) return;

      // Use RAF to batch DOM reads
      requestAnimationFrame(() => {
        const hasOverflow = detectOverflow(expandedSection.element!, container);

        if (!hasOverflow) {
          // No overflow, just scroll into view if needed
          scrollIntoViewIfNeeded(expandedSection.element!, container, {
            behavior: config.scrollBehavior,
            block: 'nearest',
          });
          return;
        }

        // Calculate how much we need to collapse
        const overflowAmount = calculateOverflowAmount(expandedSection.element!, container);

        // Get sections sorted by LRU (oldest first), excluding the one being expanded
        const sectionsToCollapse = Array.from(sections.values())
          .filter((s) => s.isOpen && s.id !== expandedSectionId)
          .sort((a, b) => a.lastUsed - b.lastUsed);

        if (sectionsToCollapse.length === 0) {
          // No sections to collapse, just scroll
          scrollIntoViewIfNeeded(expandedSection.element!, container, {
            behavior: config.scrollBehavior,
            block: 'nearest',
          });
          return;
        }

        // Collapse sections based on strategy
        if (config.collapseStrategy === 'all') {
          // Collapse all other sections immediately
          sectionsToCollapse.forEach((section) => {
            section.isOpen = false;
            onCollapse?.(section.id);
          });

          setOpenSections((prev) => {
            const next = new Set(prev);
            sectionsToCollapse.forEach((s) => next.delete(s.id));
            return next;
          });
        } else {
          // Incremental: collapse one by one until fit
          let collapsedCount = 0;
          const toCollapse: string[] = [];

          for (const section of sectionsToCollapse) {
            toCollapse.push(section.id);
            section.isOpen = false;
            collapsedCount++;

            // Re-check after each collapse (simplified - in production, measure actual heights)
            if (collapsedCount >= sectionsToCollapse.length || overflowAmount <= 0) {
              break;
            }
          }

          if (toCollapse.length > 0) {
            toCollapse.forEach((id) => onCollapse?.(id));

            setOpenSections((prev) => {
              const next = new Set(prev);
              toCollapse.forEach((id) => next.delete(id));
              return next;
            });
          }
        }

        // After collapsing, wait for animation to complete before scrolling
        // This ensures the collapsed sections have finished animating
        setTimeout(() => {
          requestAnimationFrame(() => {
            if (expandedSection.element) {
              // Check if section still overflows after collapsing
              const stillOverflows = detectOverflow(expandedSection.element, container);
              
              if (stillOverflows) {
                // Even after collapsing everything, section doesn't fit
                // Scroll to show as much of it as possible
                const elementRect = expandedSection.element.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                const elementTop = expandedSection.element.offsetTop;
                const elementHeight = expandedSection.element.offsetHeight;
                const containerHeight = container.clientHeight;
                
                // If element is taller than viewport, scroll to show top
                // Otherwise, scroll to show the part that's not visible
                if (elementHeight > containerHeight) {
                  // Element is taller than viewport - scroll to show top
                  container.scrollTo({
                    top: elementTop,
                    behavior: config.scrollBehavior,
                  });
                } else if (elementRect.top < containerRect.top) {
                  // Element is above viewport - scroll to show top
                  container.scrollTo({
                    top: elementTop,
                    behavior: config.scrollBehavior,
                  });
                } else if (elementRect.bottom > containerRect.bottom) {
                  // Element is below viewport - scroll to show bottom
                  const scrollTop = elementTop + elementHeight - containerHeight;
                  container.scrollTo({
                    top: scrollTop,
                    behavior: config.scrollBehavior,
                  });
                }
              } else {
                // Section fits now, use normal scroll behavior
                scrollIntoViewIfNeeded(expandedSection.element, container, {
                  behavior: config.scrollBehavior,
                  block: 'nearest',
                });
              }
            }
          });
        }, 300); // Match the collapse animation duration
      });
    },
    [config, containerRef, onCollapse]
  );

  /**
   * Expands a section and updates LRU timestamp
   */
  const expandSection = useCallback(
    (sectionId: string, element?: HTMLElement) => {
      const sections = sectionsRef.current;
      const section = sections.get(sectionId);

      if (section) {
        section.lastUsed = Date.now();
        section.isOpen = true;
        if (element) {
          section.element = element;
        }
      } else {
        sections.set(sectionId, {
          id: sectionId,
          element: element || null,
          lastUsed: Date.now(),
          isOpen: true,
        });
      }

      setOpenSections((prev) => new Set(prev).add(sectionId));
      onExpand?.(sectionId);

      // Debounce overflow check
      if (pendingCheckRef.current) {
        cancelAnimationFrame(pendingCheckRef.current);
      }

      pendingCheckRef.current = requestAnimationFrame(() => {
        checkOverflowAndCollapse(sectionId);
      });
    },
    [checkOverflowAndCollapse, onExpand]
  );

  /**
   * Collapses a section
   */
  const collapseSection = useCallback(
    (sectionId: string) => {
      const section = sectionsRef.current.get(sectionId);
      if (section) {
        section.isOpen = false;
      }

      setOpenSections((prev) => {
        const next = new Set(prev);
        next.delete(sectionId);
        return next;
      });

      onCollapse?.(sectionId);
    },
    [onCollapse]
  );

  /**
   * Toggles a section open/closed
   */
  const toggleSection = useCallback(
    (sectionId: string, element?: HTMLElement) => {
      const isOpen = openSections.has(sectionId);
      if (isOpen) {
        collapseSection(sectionId);
      } else {
        expandSection(sectionId, element);
      }
    },
    [openSections, collapseSection, expandSection]
  );

  /**
   * Checks if a section is open
   */
  const isSectionOpen = useCallback(
    (sectionId: string): boolean => {
      return openSections.has(sectionId);
    },
    [openSections]
  );

  // Set up ResizeObserver to handle dynamic content changes
  useEffect(() => {
    if (!config.enabled || !containerRef.current) return;

    const observerCallback = () => {
      // Debounce re-checks when content resizes
      if (pendingCheckRef.current) {
        cancelAnimationFrame(pendingCheckRef.current);
      }

      pendingCheckRef.current = requestAnimationFrame(() => {
        // Re-check the most recently expanded section
        const sections = Array.from(sectionsRef.current.values())
          .filter((s) => s.isOpen)
          .sort((a, b) => b.lastUsed - a.lastUsed);

        if (sections.length > 0 && sections[0].element) {
          checkOverflowAndCollapse(sections[0].id);
        }
      });
    };

    resizeObserverRef.current = new ResizeObserver(observerCallback);

    // Observe all registered section elements
    sectionsRef.current.forEach((section) => {
      if (section.element) {
        resizeObserverRef.current?.observe(section.element);
      }
    });

    return () => {
      resizeObserverRef.current?.disconnect();
      if (pendingCheckRef.current) {
        cancelAnimationFrame(pendingCheckRef.current);
      }
    };
  }, [config.enabled, containerRef, checkOverflowAndCollapse]);

  return {
    openSections,
    expandSection,
    collapseSection,
    toggleSection,
    registerSection,
    isSectionOpen,
  };
}

